<template>

    <!-- Project Selection -->

    <div class="col-12 form__control-group field-group">
      <label :class="{'required': field.required}" :for="`select-project-input-${parent}-${field.name}`">

        {{ field.label ? field.label : field.Label ? field.Label : 'Project' }}
        <SVGIcon
          v-if="field.description"
          v-tooltip="field.description"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>



      <select
        :id="`select-project-input-${parent}-${field.name}`"
        :value="selectedProjectId"
        class="select"
        @input="onChangeProject(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Project</option>
        <option
          v-for="(project, i) in projects"
          :key="`dropdown-project-${parent}-${field.name}-${i}-${project.id}`"
          :value="project.id"
        >
          {{ project.name }}
        </option>
      </select>
    </div>

    <!-- Experiment Selection -->
    <div v-if="selectedProjectId" class="col-12 form__control-group field-group">
      <label :for="`select-experiment-input-${parent}-${field.name}`">
        Experiment
        <SVGIcon
          v-if="field.description"
          v-tooltip="'Select an experiment'"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-experiment-input-${parent}-${field.name}`"
        :value="selectedExperimentId"
        class="select"
        @input="onChangeExperiment(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Experiment</option>
        <option
          v-for="(experiment, i) in experiments"
          :key="`dropdown-experiment-${parent}-${field.name}-${i}-${experiment.id}`"
          :value="experiment.id"
        >
          {{ experiment.name }}
        </option>
      </select>
    </div>

    <!-- Training Session Selection -->
    <div class="col-12 form__control-group field-group">
      <label :for="`select-training-session-input-${parent}-${field.name}`">
        Training Session
        <SVGIcon
          v-if="field.description"
          v-tooltip="'Select a training session'"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-training-session-input-${parent}-${field.name}`"
        :value="selectedTrainingSessionId"
        class="select"
        @input="onChangeTrainingSession(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Training Session</option>
        <option
          v-for="(session, i) in trainingSessions"
          :key="`dropdown-training-session-${parent}-${field.name}-${i}-${session.id}`"
          :value="session.id"
        >
          {{ session.name || `Session ${session.id}` }}
        </option>
      </select>
    </div>

</template>

<script setup>
import {
  ref, toRefs, onMounted, watch,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';

const props = defineProps({
  parent: {
    type: String,
    default: '',
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  field: {
    type: Object,
    default: () => {},
  },
  projectID: {
    type: [Number, String],
    default: null,
  },
  experimentID: {
    type: [Number, String],
    default: null,
  },
});

const {
  parent,
  field,
} = toRefs(props);

const emit = defineEmits(['update:modelValue']);

const projects = ref([]);
const selectedProjectId = ref(null);
const experiments = ref([]);
const selectedExperimentId = ref(null);
const trainingSessions = ref([]);
const selectedTrainingSessionId = ref(null);

// Initialize from props
watch(() => props.projectID, (newValue) => {
  if (newValue) {
    selectedProjectId.value = parseInt(newValue);
    getExperiments(newValue);
  }
}, { immediate: true });

watch(() => props.experimentID, (newValue) => {
  if (newValue) {
    selectedExperimentId.value = parseInt(newValue);
    getTrainingSessions(newValue);
  }
}, { immediate: true });

async function getProjects() {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('project.list', {});
    
    if (response.error) {
      throw response.error;
    }
    return response.result || [];
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

async function getExperiments(projectId) {
  if (!projectId) {
    experiments.value = [];
    selectedExperimentId.value = null;
    return;
  }

  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('experiment.list', {
      project_id: parseInt(projectId),
    });
    
    if (response.error) {
      throw response.error;
    }
    experiments.value = response.result || [];
  } catch (error) {
    console.error('Error fetching experiments:', error);
    experiments.value = [];
  }
}

async function getTrainingSessions(experimentId) {
  if (!experimentId) {
    trainingSessions.value = [];
    selectedTrainingSessionId.value = null;
    return;
  }

  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.getTrainingSessionList({ 
      trainer_id: parseInt(experimentId),
      reverse: true
    });
    
    if (response.error) {
      throw response.error;
    }
    trainingSessions.value = response.result || [];
  } catch (error) {
    console.error('Error fetching training sessions:', error);
    trainingSessions.value = [];
  }
}

function onChangeProject(projectId) {
  selectedProjectId.value = projectId;
  
  // Reset dependent selects
  selectedExperimentId.value = null;
  selectedTrainingSessionId.value = null;
  trainingSessions.value = [];
  
  // Fetch experiments for the selected project
  if (projectId) {
    getExperiments(projectId);
  } else {
    experiments.value = [];
  }
  
  // Emit update
  emit('update:modelValue', {
    projectID: projectId,
    experimentID: null,
    trainingSessionID: null,
  });
}

function onChangeExperiment(experimentId) {
  selectedExperimentId.value = experimentId;
  
  // Reset training sessions
  selectedTrainingSessionId.value = null;
  
  // Fetch training sessions for the selected experiment
  if (experimentId) {
    getTrainingSessions(experimentId);
  } else {
    trainingSessions.value = [];
  }
  
  // Emit update
  emit('update:modelValue', {
    projectID: selectedProjectId.value,
    experimentID: experimentId,
    trainingSessionID: null,
  });
}

function onChangeTrainingSession(sessionId) {
  selectedTrainingSessionId.value = sessionId;
  
  // Emit update
  emit('update:modelValue', {
    projectID: selectedProjectId.value,
    experimentID: selectedExperimentId.value,
    trainingSessionID: sessionId,
  });
}

onMounted(async () => {
  // Get all projects
  projects.value = await getProjects();
  
  // If projectID is provided in props, use it
  if (props.projectID) {
    selectedProjectId.value = parseInt(props.projectID);
    await getExperiments(props.projectID);
    
    // If experimentID is also provided, use it
    if (props.experimentID) {
      selectedExperimentId.value = parseInt(props.experimentID);
      await getTrainingSessions(props.experimentID);
    }
  } else if (projects.value && projects.value.length > 0) {
    // Default to first project if no projectID provided
    selectedProjectId.value = projects.value[0].id;
    await getExperiments(projects.value[0].id);
  }
});
</script>

<style scoped>
/* Inherits styles from parent schema form */
</style>
